import { APP_ROUTES } from '@/common/constants'
import { AccountType, type AddressDetails } from '@/common/types'
import { extractCountryCodeAndMobile } from '@/user/mobile/MobileUtils'
import { validateMobileNumber } from '@/onboarding/features/mobile/rules/mobileValidation'

function hasCompleteAddress(address: AddressDetails | undefined) {
    if (!address) return false

    return Boolean(
        address.streetNumber &&
            address.streetName &&
            address.city &&
            address.state &&
            address.postCode &&
            address.country,
    )
}

function hasValidMobile(mobile: string) {
    if (!mobile.trim()) return false

    const { countryCode, mobile: localMobile } = extractCountryCodeAndMobile(mobile)
    return validateMobileNumber(localMobile, countryCode).valid
}

export const APPLICATION_FLOW_IDS = {
    ADD_ACCOUNT_MODAL: 'add-account-modal',
} as const

export type ApplicationFlowId = (typeof APPLICATION_FLOW_IDS)[keyof typeof APPLICATION_FLOW_IDS]

export const APPLICATION_FLOW_STEP_IDS = {
    SELECT_ACCOUNT_TYPE: 'selectAccountType',
    PARTICIPANT_DETAILS: 'participantDetails',
    PARTICIPANT_COMMS: 'participantComms',
    PARTICIPANT_SURVEY: 'participantSurvey',
    PARTICIPANT_ACCESS_REVIEW: 'participantAccessReview',
    TERMS_AND_CONDITIONS: 'termsAndConditions',
} as const

export type ApplicationFlowStepId = (typeof APPLICATION_FLOW_STEP_IDS)[keyof typeof APPLICATION_FLOW_STEP_IDS]

export const APPLICATION_FLOW_VIEW_IDS = {
    SHARED_ACCOUNT_TYPE: 'sharedAccountType',
    SHARED_PARTICIPANT_DETAILS: 'sharedParticipantDetails',
    ADVISER_PARTICIPANT_COMMS: 'adviserParticipantComms',
    ADVISER_PARTICIPANT_SURVEY: 'adviserParticipantSurvey',
    SHARED_PARTICIPANT_ACCESS_REVIEW: 'sharedParticipantAccessReview',
    SHARED_TERMS_AND_CONDITIONS: 'sharedTermsAndConditions',
} as const

export type ApplicationFlowViewId = (typeof APPLICATION_FLOW_VIEW_IDS)[keyof typeof APPLICATION_FLOW_VIEW_IDS]

export const APPLICATION_FLOW_EVENTS = {
    CONTINUE: 'CONTINUE',
    BACK: 'BACK',
    SUBMIT: 'SUBMIT',
    EXIT: 'EXIT',
} as const

export type ApplicationFlowEvent = (typeof APPLICATION_FLOW_EVENTS)[keyof typeof APPLICATION_FLOW_EVENTS]

export const APPLICATION_FLOW_OUTCOME_IDS = {
    CLOSE_MODAL: 'closeModal',
    PROCEED_TO_KYC_CONFIRM: 'proceedToKycConfirm',
} as const

export type ApplicationFlowOutcomeId = (typeof APPLICATION_FLOW_OUTCOME_IDS)[keyof typeof APPLICATION_FLOW_OUTCOME_IDS]

export type ApplicationFlowRoute = typeof APP_ROUTES.APPLICATION_CREATE | typeof APP_ROUTES.APPLICATION_KYC

export type ApplicationFlowContext = {
    isPrimaryAdviserUser: boolean
    participantAccessProfile?: string
    participantDateOfBirth: string
    participantEmail: string
    participantMobile: string
    participantFirstName: string
    participantHasDifferentPostalAddress: boolean
    participantLastName: string
    participantPostalAddress?: AddressDetails
    participantResidentialAddress?: AddressDetails
    selectedAccountType?: AccountType
    participantOccupation?: string
    participantInvestmentPurpose?: string
    participantExpectedTradeFrequency?: string
    participantExpectedTradeAmount?: string
}

export const APPLICATION_FLOW_CONDITION_IDS = {
    HAS_SELECTED_ACCOUNT_TYPE: 'hasSelectedAccountType',
    HAS_PARTICIPANT_DETAILS: 'hasParticipantDetails',
    HAS_PARTICIPANT_ACCESS_PROFILE: 'hasParticipantAccessProfile',
    HAS_PARTICIPANT_SURVEY: 'hasParticipantSurvey',
    IS_PRIMARY_ADVISER_USER: 'isPrimaryAdviserUser',
    IS_NOT_PRIMARY_ADVISER_USER: 'isNotPrimaryAdviserUser',
} as const

export type ApplicationFlowConditionId =
    (typeof APPLICATION_FLOW_CONDITION_IDS)[keyof typeof APPLICATION_FLOW_CONDITION_IDS]

export type ApplicationFlowCondition = (context: ApplicationFlowContext) => boolean

export const APPLICATION_FLOW_CONDITIONS: Record<ApplicationFlowConditionId, ApplicationFlowCondition> = {
    [APPLICATION_FLOW_CONDITION_IDS.HAS_SELECTED_ACCOUNT_TYPE]: (context) => Boolean(context.selectedAccountType),
    [APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_DETAILS]: (context) =>
        Boolean(
            context.participantFirstName.trim() &&
                context.participantLastName.trim() &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(context.participantEmail.trim()) &&
                hasValidMobile(context.participantMobile) &&
                context.participantDateOfBirth.trim() &&
                hasCompleteAddress(context.participantResidentialAddress) &&
                (!context.participantHasDifferentPostalAddress || hasCompleteAddress(context.participantPostalAddress)),
        ),
    [APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_ACCESS_PROFILE]: (context) =>
        Boolean(context.participantAccessProfile),
    [APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_SURVEY]: (context) =>
        Boolean(
            context.participantOccupation &&
                context.participantInvestmentPurpose &&
                context.participantExpectedTradeFrequency &&
                context.participantExpectedTradeAmount,
        ),
    [APPLICATION_FLOW_CONDITION_IDS.IS_PRIMARY_ADVISER_USER]: (context) => context.isPrimaryAdviserUser,
    [APPLICATION_FLOW_CONDITION_IDS.IS_NOT_PRIMARY_ADVISER_USER]: (context) => !context.isPrimaryAdviserUser,
}

export type ApplicationFlowGuardRef = ApplicationFlowConditionId | readonly ApplicationFlowConditionId[]

export type ApplicationFlowTarget =
    | { kind: 'step'; stepId: ApplicationFlowStepId }
    | { kind: 'route'; route: ApplicationFlowRoute }
    | { kind: 'outcome'; outcome: ApplicationFlowOutcomeId }

export type ApplicationFlowTransition = {
    target: ApplicationFlowTarget
    when?: ApplicationFlowGuardRef
}

export type ApplicationFlowRedirectRule = {
    when: ApplicationFlowGuardRef
    target: ApplicationFlowTarget
}

export type ApplicationFlowViewEntry = {
    view: ApplicationFlowViewId
    when?: ApplicationFlowGuardRef
    supportedAccountTypes?: AccountType[]
    supportedAccessProfiles?: string[]
}

export type ApplicationFlowActionDefinition = {
    label: string
    event: ApplicationFlowEvent
    variant: 'default' | 'secondary' | 'ghost' | 'outline'
    enabledWhen?: ApplicationFlowGuardRef
}

export type ApplicationFlowUiDefinition = {
    layout: 'page' | 'modal'
    route?: ApplicationFlowRoute
    title: string
    progressPercentage?: number
    views: readonly ApplicationFlowViewEntry[]
    primaryAction?: ApplicationFlowActionDefinition
    secondaryAction?: ApplicationFlowActionDefinition
    tertiaryAction?: ApplicationFlowActionDefinition
}

export type ApplicationFlowStepDefinition = {
    id: ApplicationFlowStepId
    ui: ApplicationFlowUiDefinition
    redirects?: readonly ApplicationFlowRedirectRule[]
    on?: Partial<Record<ApplicationFlowEvent, readonly ApplicationFlowTransition[]>>
}

export type ApplicationFlowDefinition = {
    id: ApplicationFlowId
    initialStep: ApplicationFlowStepId
    steps: Partial<Record<ApplicationFlowStepId, ApplicationFlowStepDefinition>>
}

export const APPLICATION_FLOW_DEFINITIONS = {
    [APPLICATION_FLOW_IDS.ADD_ACCOUNT_MODAL]: {
        id: APPLICATION_FLOW_IDS.ADD_ACCOUNT_MODAL,
        initialStep: APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE,
        steps: {
            [APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE]: {
                id: APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE,
                ui: {
                    layout: 'modal',
                    title: 'Create application',
                    progressPercentage: 25,
                    views: [
                        {
                            view: APPLICATION_FLOW_VIEW_IDS.SHARED_ACCOUNT_TYPE,
                            when: APPLICATION_FLOW_CONDITION_IDS.IS_PRIMARY_ADVISER_USER,
                            supportedAccountTypes: [AccountType.INDIVIDUAL],
                        },
                        {
                            view: APPLICATION_FLOW_VIEW_IDS.SHARED_ACCOUNT_TYPE,
                            when: APPLICATION_FLOW_CONDITION_IDS.IS_NOT_PRIMARY_ADVISER_USER,
                        },
                    ],
                    primaryAction: {
                        label: 'Next',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                        enabledWhen: APPLICATION_FLOW_CONDITION_IDS.HAS_SELECTED_ACCOUNT_TYPE,
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS,
                            },
                            when: APPLICATION_FLOW_CONDITION_IDS.IS_PRIMARY_ADVISER_USER,
                        },
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.PROCEED_TO_KYC_CONFIRM,
                            },
                            when: APPLICATION_FLOW_CONDITION_IDS.IS_NOT_PRIMARY_ADVISER_USER,
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },

            [APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS]: {
                id: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS,
                ui: {
                    layout: 'modal',
                    title: 'Account details',
                    progressPercentage: 50,
                    views: [{ view: APPLICATION_FLOW_VIEW_IDS.SHARED_PARTICIPANT_DETAILS }],
                    primaryAction: {
                        label: 'Next',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                        enabledWhen: APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_DETAILS,
                    },
                    secondaryAction: {
                        label: 'Back',
                        event: APPLICATION_FLOW_EVENTS.BACK,
                        variant: 'outline',
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.BACK]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.SELECT_ACCOUNT_TYPE,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_COMMS,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },
            [APPLICATION_FLOW_STEP_IDS.PARTICIPANT_COMMS]: {
                id: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_COMMS,
                ui: {
                    layout: 'modal',
                    title: 'Client communications',
                    progressPercentage: 55,
                    views: [{ view: APPLICATION_FLOW_VIEW_IDS.ADVISER_PARTICIPANT_COMMS }],
                    primaryAction: {
                        label: 'Next',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                    },
                    secondaryAction: {
                        label: 'Back',
                        event: APPLICATION_FLOW_EVENTS.BACK,
                        variant: 'outline',
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.BACK]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_ACCESS_REVIEW,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },
            [APPLICATION_FLOW_STEP_IDS.PARTICIPANT_SURVEY]: {
                id: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_SURVEY,
                ui: {
                    layout: 'modal',
                    title: 'Your investing',
                    progressPercentage: 75,
                    views: [{ view: APPLICATION_FLOW_VIEW_IDS.ADVISER_PARTICIPANT_SURVEY }],
                    primaryAction: {
                        label: 'Next',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                        enabledWhen: APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_SURVEY,
                    },
                    secondaryAction: {
                        label: 'Back',
                        event: APPLICATION_FLOW_EVENTS.BACK,
                        variant: 'outline',
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.BACK]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_ACCESS_REVIEW,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.TERMS_AND_CONDITIONS,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },
            [APPLICATION_FLOW_STEP_IDS.PARTICIPANT_ACCESS_REVIEW]: {
                id: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_ACCESS_REVIEW,
                ui: {
                    layout: 'modal',
                    title: 'Review participant access',
                    progressPercentage: 62,
                    views: [
                        {
                            view: APPLICATION_FLOW_VIEW_IDS.SHARED_PARTICIPANT_ACCESS_REVIEW,
                            supportedAccessProfiles: ['TRADE', 'NONE'],
                        },
                    ],
                    primaryAction: {
                        label: 'Next',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                        enabledWhen: APPLICATION_FLOW_CONDITION_IDS.HAS_PARTICIPANT_ACCESS_PROFILE,
                    },
                    secondaryAction: {
                        label: 'Back',
                        event: APPLICATION_FLOW_EVENTS.BACK,
                        variant: 'outline',
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.BACK]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_COMMS,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_SURVEY,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },
            [APPLICATION_FLOW_STEP_IDS.TERMS_AND_CONDITIONS]: {
                id: APPLICATION_FLOW_STEP_IDS.TERMS_AND_CONDITIONS,
                ui: {
                    layout: 'modal',
                    title: 'Terms and conditions',
                    progressPercentage: 90,
                    views: [{ view: APPLICATION_FLOW_VIEW_IDS.SHARED_TERMS_AND_CONDITIONS }],
                    primaryAction: {
                        label: 'I agree',
                        event: APPLICATION_FLOW_EVENTS.CONTINUE,
                        variant: 'default',
                    },
                    secondaryAction: {
                        label: 'Back',
                        event: APPLICATION_FLOW_EVENTS.BACK,
                        variant: 'outline',
                    },
                    tertiaryAction: {
                        label: 'Cancel',
                        event: APPLICATION_FLOW_EVENTS.EXIT,
                        variant: 'ghost',
                    },
                },
                on: {
                    [APPLICATION_FLOW_EVENTS.BACK]: [
                        {
                            target: {
                                kind: 'step',
                                stepId: APPLICATION_FLOW_STEP_IDS.PARTICIPANT_SURVEY,
                            },
                            when: APPLICATION_FLOW_CONDITION_IDS.IS_PRIMARY_ADVISER_USER,
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.CONTINUE]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.PROCEED_TO_KYC_CONFIRM,
                            },
                        },
                    ],
                    [APPLICATION_FLOW_EVENTS.EXIT]: [
                        {
                            target: {
                                kind: 'outcome',
                                outcome: APPLICATION_FLOW_OUTCOME_IDS.CLOSE_MODAL,
                            },
                        },
                    ],
                },
            },
        },
    },
} satisfies Record<ApplicationFlowId, ApplicationFlowDefinition>
